import * as React from 'react';
import EcoSpace from 'eco-design/base/eco-space';
import EcoSectionCommon from 'eco-design/elements/eco-section-common';
import EcoImg from 'eco-design/base/eco-img';
import EcoCountDown from 'eco-design/elements/eco-count-down';
import { getIsMobileWidth } from '@elem/utils/devices';
import { useRouter } from 'next/router';
import { AnimatedGrid, AnimatedSection } from 'elem/compPage/delta-3-max-series/pages/product/us/StatusA/Components/AnimatedSection';
import 'elem/compPage/delta-3-max-series/pages/product/us/StatusA/Components/AnimatedSection/scroll-animations.scss';
import EcoEmail from '../EcoEmail';
import Modal from '../Modal';
import styles from './index.module.scss';
import { useTerms } from './conf';
import { useLang } from '@/i18n';
import getLocal from '@/app/utils/getLocal';
import gtm from '@/utils/gtm';

// 订阅结束时间 YYYY/MM/DD 限制，其他格式苹果浏览器不识别
const END_TIME = new Date(`2025/10/13 15:00:00 GMT+8`).getTime();

const CONTACT_LIST_ID_MAP = {
    us: ['1309693895'],
    ca: ['1525424752'],
} as const;

const getEmailFields = (locale: string) => [
    {
        id: locale === 'us' ? '4423' : '4424',
        type: 'single' as const,
        value: 1,
        overwrite: true,
    },
    {
        id: '31',
        type: 'single' as const,
        value: 1,
        overwrite: false,
    },
    {
        id: '11150',
        type: 'multiple' as const,
        value: [25],
        overwrite: false,
    },
    {
        id: '13680',
        type: 'text' as const,
        value: new Date().toISOString().split('T')[0],
        overwrite: false,
    },
];

const Subscribe: React.FC = () => {
    const { t } = useLang('Subscribe');
    const { locale } = getLocal();
    const [show, setShow] = React.useState(false);
    const { query } = useRouter();
    const isMobile = getIsMobileWidth();
    const terms = useTerms(locale);

    const extTimer = React.useMemo(() => {
        const { _timer } = query;
        if (!_timer) return END_TIME;
        return new Date(`${_timer} GMT+8`).getTime();
    }, [query]);

    const [isExpiredState, setIsExpiredState] = React.useState(() => extTimer < Date.now());

    const cards = React.useMemo(
        () => [
            {
                title: t.rich('card_title1', {
                    strong: (text: string) => <strong>{text}</strong>,
                }),
                img: 'https://websiteoss.ecoflow.com/figma-uploads/1758249938690-f2ypm0-compressed-1.png',
            },
            {
                title: t(`card_title2`),
                img: 'https://websiteoss.ecoflow.com/figma-uploads/1757758612989-ay8rok-compressed-2.png',
                h5: 'https://websiteoss.ecoflow.com/figma-uploads/1758687701694-gbwvem-compressed-1921.png',
            },
            {
                title: t(`card_title3`),
                img:
                    locale === 'us'
                        ? 'https://websiteoss.ecoflow.com/figma-uploads/1758249969705-pw4vyq-compressed-3.png'
                        : 'https://websiteoss.ecoflow.com/figma-uploads/1758333210727-ajad5x-compressed-768_light_%E4%BE%A7%E6%BB%91.png',
            },
            {
                title: t(`card_title4`),
                img: 'https://websiteoss.ecoflow.com/figma-uploads/1758249985083-o1r2xh-compressed-4.png',
            },
            {
                title: t(`card_title5`),
                img: 'https://websiteoss.ecoflow.com/figma-uploads/1758249999785-avadvb-compressed-5.png',
            },
        ],
        [isMobile, locale, t],
    );

    const contactListId = React.useMemo(() => {
        return CONTACT_LIST_ID_MAP[locale as keyof typeof CONTACT_LIST_ID_MAP] || [];
    }, [locale]);

    const emailFields = React.useMemo(() => getEmailFields(locale), [locale]);

    const handleViewTerms = () => {
        gtm.push({
            event: 'uaEvent',
            event_name: 'terms_confirm',
            button_name: t('footerDesc'),
            url: 'none',
        });
        setShow(true);
    };

    const handleCloseModal = () => {
        setShow(false);
    };

    const onCountdownEnd = React.useCallback(() => {
        // 倒计时结束时，检查当前时间是否大于结束时间
        const currentTime = Date.now();
        if (currentTime > extTimer) {
            setIsExpiredState(true);
        } else {
            setIsExpiredState(false);
        }
    }, [extTimer]);

    if (isExpiredState) {
        return null;
    }

    return (
        <div className={`${styles.Subscribe} GtmModuleData-section`} data-section-id="d3c-Subscribe">
            <div className="subscribe-content">
                <AnimatedSection variant="fadeInUp" options={{ delay: 200 }} className="d3m-title-common">
                    <EcoSectionCommon title={t('title')} desc="" />
                    <div className="countdown-content">
                        <div className="countdown-row">
                            <EcoCountDown endTime={END_TIME} onCountdownEnd={onCountdownEnd} />
                        </div>
                        <div className="digital-content">
                            <div className="digital-text">{t('day')}</div>
                            <div className="digital-text">{t('hour')}</div>
                            <div className="digital-text">{t('minute')}</div>
                            <div className="digital-text">{t('second')}</div>
                        </div>
                    </div>
                    <div className="subscribe-container">
                        <EcoEmail
                            contactListId={[...contactListId]}
                            defaultTermsCheck={true}
                            fields={emailFields}
                            needTerms={true}
                            tipsObj={{
                                btnText: t('btnText'),
                                placeholder: t('placeholder'),
                                successTips: t('successTips'),
                            }}
                        />
                    </div>
                </AnimatedSection>

                <div className="cards">
                    <AnimatedGrid
                        variant="rotateIn"
                        options={{
                            stagger: 0.1,
                            threshold: 0.2,
                            delay: 200,
                        }}
                        className="card-list pc-only"
                        itemClassName="card-item-animated"
                    >
                        {cards.map((item, index) => (
                            <div className={`card-item card-item-${index + 1}`} key={`card-${index}`}>
                                <div className="card-item-title">{item.title}</div>
                                <div className="card-item-img">
                                    <EcoImg pc={item.img} h5={item.h5 ? item.h5 : ''} alt={item.title} isOpenLazy={true} />
                                </div>
                            </div>
                        ))}
                    </AnimatedGrid>
                </div>
                <div className="view-terms" onClick={handleViewTerms}>
                    {t('viewTerms')}
                </div>
            </div>

            <EcoSpace />

            <Modal className={styles.TermsModal} onCancel={handleCloseModal} open={show}>
                <AnimatedSection variant="scaleIn" options={{ triggerOnce: false }}>
                    <div className="modal-title">{t('modal_title')}</div>
                    <div className="modal-content">
                        {terms.map((item, index) => {
                            if (item === '\n') return <br />;
                            return <div className="item-content" key={`term-${index}`} dangerouslySetInnerHTML={{ __html: item }} />;
                        })}
                    </div>
                </AnimatedSection>
            </Modal>
        </div>
    );
};

export default Subscribe;
